import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRuler, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

// EU ring sizes with inner diameter in mm
const RING_SIZES = [
  { eu: 48, mm: 15.3 },
  { eu: 49, mm: 15.6 },
  { eu: 50, mm: 15.9 },
  { eu: 51, mm: 16.2 },
  { eu: 52, mm: 16.5 },
  { eu: 53, mm: 16.9 },
  { eu: 54, mm: 17.2 },
  { eu: 55, mm: 17.5 },
  { eu: 56, mm: 17.8 },
  { eu: 57, mm: 18.1 },
  { eu: 58, mm: 18.5 },
  { eu: 59, mm: 18.8 },
  { eu: 60, mm: 19.1 },
  { eu: 61, mm: 19.4 },
  { eu: 62, mm: 19.7 },
];

export { RING_SIZES };

const RingSizeGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  const content = (
    <>
      {/* How to measure */}
      <div className="mb-5 p-4 bg-brand-sand/50 dark:bg-brand-charcoal rounded-lg">
        <h4 className="font-semibold text-sm text-black dark:text-white mb-2">
          {t('ringSize.howToMeasure')}
        </h4>
        <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1.5 list-decimal list-inside">
          <li>{t('ringSize.step1')}</li>
          <li>{t('ringSize.step2')}</li>
          <li>{t('ringSize.step3')}</li>
        </ol>
      </div>

      {/* Size table */}
      <div className="rounded-lg border border-gray-100 dark:border-brand-charcoal overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-brand-charcoal">
              <th className="text-left px-4 py-2.5 text-black dark:text-white font-semibold text-xs uppercase tracking-wider">
                {t('ringSize.euSize')}
              </th>
              <th className="text-right px-4 py-2.5 text-black dark:text-white font-semibold text-xs uppercase tracking-wider">
                {t('ringSize.diameter')}
              </th>
            </tr>
          </thead>
          <tbody>
            {RING_SIZES.map((size, i) => (
              <tr
                key={size.eu}
                className={i % 2 === 0
                  ? 'bg-white dark:bg-brand-dark'
                  : 'bg-gray-50/50 dark:bg-brand-charcoal/30'}
              >
                <td className="px-4 py-2 text-black dark:text-white font-medium">
                  EU {size.eu}
                </td>
                <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">
                  {size.mm.toFixed(1)} mm
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-4 pb-2">
        {t('ringSize.tip')}
      </p>
    </>
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="text-sm text-brand-blue hover:underline flex items-center gap-1"
      >
        <FontAwesomeIcon icon={faRuler} className="h-3 w-3" />
        {t('ringSize.guide')}
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            isMobile ? (
              /* ===== Mobile: full-screen overlay (same as cart/hamburger) ===== */
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="fixed inset-0 z-[90] bg-white dark:bg-brand-dark md:hidden overflow-y-auto"
              >
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100 dark:border-brand-charcoal bg-white dark:bg-brand-dark">
                  <h3 className="text-lg font-bold text-black dark:text-white">
                    {t('ringSize.guideTitle')}
                  </h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 -mr-2 text-gray-400 hover:text-black dark:hover:text-white transition-colors"
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                  </button>
                </div>

                {/* Content — scrolls naturally within the full-screen div */}
                <div className="px-6 py-4">
                  {content}
                </div>
              </motion.div>
            ) : (
              /* ===== Desktop: backdrop + centered modal ===== */
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm
                  flex items-center justify-center"
                onClick={() => setIsOpen(false)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-[440px] max-h-[80vh] rounded-xl
                    bg-white dark:bg-brand-dark shadow-2xl
                    flex flex-col overflow-hidden"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100 dark:border-brand-charcoal shrink-0">
                    <h3 className="text-lg font-bold text-black dark:text-white">
                      {t('ringSize.guideTitle')}
                    </h3>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 -mr-2 text-gray-400 hover:text-black dark:hover:text-white transition-colors"
                    >
                      <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Scrollable content */}
                  <div className="overflow-y-auto overscroll-contain px-6 py-4 flex-1 min-h-0">
                    {content}
                  </div>
                </motion.div>
              </motion.div>
            )
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default RingSizeGuide;
